import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// @mui
import {
  Box,
  Input,
  Paper,
  Typography,
  InputAdornment,
  IconButton,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
} from '@mui/material';
// components
import Iconify from './Iconify';
import { DOMAIN } from '../redux/constants';
import useResponsive from '../hooks/useResponsive';
import { RED } from '../utils/brandColors';
import getDeviceId from '../utils/deviceId';

// ----------------------------------------------------------------------

export default function SearchBar() {
  const navigate = useNavigate();
  const isDesktop = useResponsive('up', 'md');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  // Distinguishes "the search failed" from "the search returned nothing" so
  // network errors don't masquerade as an empty result set.
  const [searchError, setSearchError] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [focused, setFocused] = useState(false);

  const debounceRef = useRef(null);
  // Monotonic request counter. If a later search starts while an earlier
  // one is still in flight and the older response arrives second, we drop
  // it instead of clobbering the fresh results with stale data.
  const requestIdRef = useRef(0);

  const [trendingTerms, setTrendingTerms] = useState(['worldcup', 'food', 'sports', 'tech', 'lifestyle', 'entertainment', 'random']);

  const fetchResults = useCallback((term) => {
    if (!term || term.trim().length === 0) {
      setResults([]);
      setShowResults(false);
      return;
    }
    const headers = {
      'Content-type': 'application/json',
      'X-Device-Id': getDeviceId(),
    };
    const userInfo = localStorage.getItem('userInfo')
      ? JSON.parse(localStorage.getItem('userInfo'))
      : null;
    if (userInfo && userInfo.access) {
      headers.Authorization = `Bearer ${userInfo.access}`;
    }
    requestIdRef.current += 1;
    const myId = requestIdRef.current;
    axios
      .get(`${DOMAIN}/api/v1/game/questions/search?q=${encodeURIComponent(term)}`, { headers })
      .then((res) => {
        if (myId !== requestIdRef.current) return; // stale response
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setSearchError(false);
        setResults(data.slice(0, 5));
        setShowResults(true);
      })
      .catch(() => {
        if (myId !== requestIdRef.current) return;
        setResults([]);
        setSearchError(true);
        setShowResults(true);
      });
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(val), 300);
  };

  const handleSelect = (id) => {
    setShowResults(false);
    setQuery('');
    setExpanded(false);
    navigate(`/v/${id}`, { state: { fromFeed: true, source: 'search' } });
  };

  const handleClickAway = (event) => {
    if (event && event.target && !document.body.contains(event.target)) {
      return;
    }
    setShowResults(false);
    setFocused(false);
    if (!isDesktop) setExpanded(false);
  };

  const handleTrendingClick = (term) => {
    setQuery(term);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    fetchResults(term);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowResults(false);
      setQuery('');
      if (!isDesktop) setExpanded(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${DOMAIN}/api/v1/engagement/categories/`)
      .then((res) => {
        const cats = Array.isArray(res.data) ? res.data : [];
        if (cats.length > 0) {
          const topCats = [...cats]
            .sort((a, b) => (b.question_count || 0) - (a.question_count || 0))
            .slice(0, 6)
            .map((c) => c.name.toLowerCase());
          if (topCats.length > 0) {
            setTrendingTerms(topCats);
          }
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    }
  , []);

  // Failed lookups get distinct copy so they don't read as an empty result set.
  const emptyCopy = searchError
    ? 'Search unavailable — check your connection'
    : 'No results found';

  // Mobile: icon button that expands
  if (!isDesktop && !expanded) {
    return (
      <IconButton
        onClick={() => setExpanded(true)}
        aria-label="Search"
        sx={{
          color: 'text.primary',
          transition: 'all 0.2s ease',
        }}
      >
        <Iconify icon="eva:search-fill" width={22} height={22} />
      </IconButton>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: 'relative' }}>
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: isDesktop ? 280 : '100%',
          // Mobile expanded: drop below the navbar at full width. Navbar is
          // 56px tall — the bar slides down into its own row so the logo
          // wordmark stays visible above.
          ...((!isDesktop && expanded) && {
            position: 'fixed',
            top: 56,
            left: 0,
            right: 0,
            zIndex: 1300,
            width: 'auto',
            bgcolor: 'background.default',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            animation: 'searchDrop 220ms cubic-bezier(0.22, 1, 0.36, 1) both',
            '@keyframes searchDrop': {
              '0%':   { transform: 'translateY(-100%)', opacity: 0 },
              '100%': { transform: 'translateY(0)',      opacity: 1 },
            },
          }),
        }}
      >
        <Input
          ref={inputRef}
          autoFocus={!isDesktop && expanded}
          fullWidth
          disableUnderline
          placeholder="Search questions..."
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          startAdornment={
            <InputAdornment position="start">
              <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
            </InputAdornment>
          }
          endAdornment={
            !isDesktop && expanded ? (
              <IconButton size="small" aria-label="Clear search" onClick={() => { setExpanded(false); setQuery(''); setShowResults(false); }}>
                <Iconify icon="eva:close-fill" width={18} height={18} />
              </IconButton>
            ) : null
          }
          sx={{
            px: (!isDesktop && expanded) ? 2 : 1.5,
            py: (!isDesktop && expanded) ? 1.25 : 0.5,
            // Blend completely with navbar background
            bgcolor: 'transparent',
            border: 'none',
            borderRadius: (!isDesktop && expanded) ? 0 : '8px',
            // 16px minimum on mobile — iOS Safari zooms inputs below 16px.
            fontSize: isDesktop ? '0.9rem' : '1rem',
          }}
        />

        {focused && !query.trim() && !showResults && (
          <Paper
            elevation={(!isDesktop && expanded) ? 0 : 3}
            sx={{
              position: (!isDesktop && expanded) ? 'static' : 'absolute',
              top: '100%', left: 0, right: 0, mt: 0, zIndex: 20,
              px: (!isDesktop && expanded) ? 2 : 1.5,
              pt: (!isDesktop && expanded) ? 0 : 1.5,
              pb: (!isDesktop && expanded) ? 1.5 : 1.5,
              borderRadius: (!isDesktop && expanded) ? 0 : '0 0 12px 12px',
              bgcolor: (!isDesktop && expanded) ? 'background.default' : 'background.paper',
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'text.disabled',
                display: 'block',
                mt: (!isDesktop && expanded) ? 1.25 : 0,
                mb: 1, px: 0.5,
              }}
            >
              Trending
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {trendingTerms.map((t) => (
                <Box
                  key={t}
                  onClick={() => handleTrendingClick(t)}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    px: 1.25, py: 0.5, borderRadius: '999px',
                    bgcolor: 'action.hover', cursor: 'pointer',
                    fontSize: '0.8rem', fontWeight: 600, color: 'text.primary',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <Iconify icon="eva:trending-up-outline" width={14} height={14} sx={{ color: RED }} />
                  {t}
                </Box>
              ))}
            </Box>
          </Paper>
        )}
        {showResults && (
          <Paper
            elevation={(!isDesktop && expanded) ? 0 : 3}
            sx={{
              position: (!isDesktop && expanded) ? 'static' : 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 0,
              zIndex: 20,
              maxHeight: 320,
              overflowY: 'auto',
              borderRadius: (!isDesktop && expanded) ? 0 : '0 0 12px 12px',
              bgcolor: (!isDesktop && expanded) ? 'background.default' : 'background.paper',
            }}
          >
            {results.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {emptyCopy}
                </Typography>
              </Box>
            ) : (
              <>
                <List disablePadding>
                  {results.map((item) => (
                    <ListItemButton
                      key={item.id}
                      onClick={() => handleSelect(item.id)}
                      sx={{ px: 2, py: 1 }}
                    >
                      <ListItemText
                        primary={item.title}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                      />
                    </ListItemButton>
                  ))}
                </List>
                <Divider />
                <Button
                  fullWidth
                  size="small"
                  onClick={() => {
                    setShowResults(false);
                    setExpanded(false);
                  }}
                  sx={{ py: 1, color: 'text.secondary', textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Close
                </Button>
              </>
            )}
          </Paper>
        )}
      </Box>
      </Box>
    </ClickAwayListener>
  );
}
